import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {
  isAnalysisTemplatesEnabled,
  isAnalysisToolRedesignEnabled,
} from 'analysis-tool/AnalysisToolFeatureGates';
import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import refreshTemplateSelectorQuery from 'analysis-tool/templates/refreshTemplateSelectorQuery';
import PollingAnalysis from 'analysis-tool/ui/PollingAnalysis';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {fetchQueryApiIntrospection} from 'query-api/useQueryApiIntrospection';
import {TagSchemaSelectorQuery} from 'schemas/shared/__generated/TagSchemaSelectorQuery';
import {TAG_SCHEMA_SELECTOR_QUERY} from 'schemas/shared/TagSchemaOrInterfaceSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHT} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';
import TabLayout, {TabLayoutBackgroundColor} from 'ui-library/TabLayout';
import {Optional} from 'util/Types';

import AnalysisRightSidebarRouter from '../right-sidebar/AnalysisRightSidebarRouter';
import AnalysisUIHeader from './AnalysisUIHeader';
import useAnalysisUISidebarState from './hooks/useAnalysisUISidebarState';
import useRightSidebarItem from './hooks/useRightSidebarItem';
import useSelectedAnalysisItem from './hooks/useSelectedAnalysisItem';
import AnalysisUIMainViewRouter from './main-view/AnalysisUIMainViewRouter';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  isStepInitializationAnalysisItem,
  isViewStepConfigAnalysisItem,
} from './main-view/MainViewTypes';
import AnalysisUISidebar from './sidebar/AnalysisUISidebar';
import {DEFAULT_HORIZONTAL_PADDING} from './sidebar/AnalysisUISidebarConstants';

interface BaseAnalysisUISkeletonProps {
  clientStateId: string;
}

export interface PresentationalAnalysisUISkeletonProps extends BaseAnalysisUISkeletonProps {
  analysis: AnalysisGraphQL;
}

export interface AnalysisUISkeletonProps extends BaseAnalysisUISkeletonProps {
  analysisId: string;
}

export function PresentationalAnalysisUISkeleton({
  clientStateId,
  analysis,
}: PresentationalAnalysisUISkeletonProps): JSX.Element {
  const sidebarState = useAnalysisUISidebarState(clientStateId);
  const selectedItemState = useSelectedAnalysisItem(clientStateId, analysis);
  const {selectedRightSidebarItem, setSelectedRightSidebarItem} = useRightSidebarItem(clientStateId);
  const isReconfigurationView = isViewStepConfigAnalysisItem(selectedItemState.selectedItem);
  const isSidebarOpen =
    sidebarState.isOpen &&
    !isStepInitializationAnalysisItem(selectedItemState.selectedItem) &&
    !isReconfigurationView;

  function openStepInitializationView(newSelectedItem: AnalysisMainViewItem): void {
    selectedItemState.onChangeSelectedItem(newSelectedItem);
    sidebarState.close();
  }

  function closeStepInitializationView(newSelectedItem?: Optional<AnalysisMainViewItem>): void {
    // Reopen the sidebar after returning to the default view, to very obviously put the user back into the
    // Analysis UI experience.
    selectedItemState.onChangeSelectedItem(
      newSelectedItem ?? {type: AnalysisMainViewItemType.EMPTY_ANALYSIS}
    );
    sidebarState.open();
  }

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'AnalysisUISkeleton', element: 'root'})}
    >
      {isSidebarOpen && (
        <div
          className={classNames('scrollbar-override', css(styles.sidebarWrapper))}
          {...getTestProps({component: 'AnalysisUISkeleton', element: 'sidebarRoot'})}
        >
          <AnalysisUISidebar
            analysis={analysis}
            onCloseSidebar={sidebarState.close}
            onOpenStepInitializationView={openStepInitializationView}
            selectedItemState={selectedItemState}
            onChangeSelectedRightSidebarItem={setSelectedRightSidebarItem}
          />
        </div>
      )}
      <TabLayout
        backgroundColor={TabLayoutBackgroundColor.GRAY_LIGHTER}
        className={css(styles.bodyWrapper)}
        header={
          <AnalysisUIHeader
            analysis={analysis}
            showOpenSidebarButton={
              !isSidebarOpen &&
              !isStepInitializationAnalysisItem(selectedItemState.selectedItem) &&
              !isReconfigurationView
            }
            onOpenSidebar={sidebarState.open}
            onOpenStepInitializationView={openStepInitializationView}
            selectedItem={selectedItemState.selectedItem}
            onChangeSelectedItem={selectedItemState.onChangeSelectedItem}
          />
        }
        isHeaderFrozen={true}
        dataTestAttrs={{component: 'AnalysisUISkeleton', element: 'tabLayout'}}
      >
        <div className={css(styles.contentWrapper)}>
          <div className={classNames(css(styles.mainViewWrapper), 'scrollbar-override')}>
            <AnalysisUIMainViewRouter
              analysis={analysis}
              clientStateId={clientStateId}
              selectedItem={selectedItemState.selectedItem}
              onChangeSelectedItem={selectedItemState.onChangeSelectedItem}
              onChangeSelectedRightSidebarItem={setSelectedRightSidebarItem}
              onCloseStepInitializationView={closeStepInitializationView}
              onOpenStepInitializationView={openStepInitializationView}
            />
          </div>
          {isAnalysisToolRedesignEnabled() && (
            <AnalysisRightSidebarRouter
              analysis={analysis}
              clientStateId={clientStateId}
              selectedRightSidebarItem={selectedRightSidebarItem}
              onChangeSelectedRightSidebarItem={setSelectedRightSidebarItem}
              onChangeSelectedItem={selectedItemState.onChangeSelectedItem}
            />
          )}
        </div>
      </TabLayout>
    </div>
  );
}

const MemoizedPresentationalAnalysisUISkeleton = React.memo(PresentationalAnalysisUISkeleton);

export default function QueryingAnalysisUISkeleton(props: AnalysisUISkeletonProps): JSX.Element {
  // Eagerly start loading Query API Introspection and schema dropdown data, which can take a long time to load.
  // Doing this now prevents a long loading experience when the user creates a dataframe using the Query API
  // form, which we expect to be a common path.
  useOnMount(() => {
    fetchQueryApiIntrospection();
    doQuerySafe<TagSchemaSelectorQuery>({query: TAG_SCHEMA_SELECTOR_QUERY});
    if (isAnalysisTemplatesEnabled()) {
      refreshTemplateSelectorQuery('cache-first');
    }
  });
  return (
    <PollingAnalysis analysisId={props.analysisId}>
      {(analysis) => <MemoizedPresentationalAnalysisUISkeleton {...props} analysis={analysis} />}
    </PollingAnalysis>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
  },
  sidebarWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 300px',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    // Right-padding should be managed by the sidebar, since it has some elements that should be padded
    // (like the sidebar content) and some that should not be (like the collapse button)
    padding: `12px 0px 16px ${DEFAULT_HORIZONTAL_PADDING}px`,
    overflow: 'auto',
  },
  bodyWrapper: {
    flex: 1,
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    position: 'relative',
    overflowX: 'hidden',
  },
  mainViewWrapper: {
    // If changes are made to this style, make sure to test extensively, since different views have different needs from its parent.
    // Empty Analysis is a short view, Create outputs has multiple panes, etc.
    flex: 1,
    height: '100%',
    maxWidth: '100%',
    minWidth: 1,
  },
});
